import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import inventoryService from "../../services/inventoryService";

// Purchase Orders
export const createPurchaseOrder = createAsyncThunk(
  "inventory/createPurchaseOrder",
  async (purchaseOrderData, { rejectWithValue }) => {
    try {
      const response = await inventoryService.createPurchaseOrder(
        purchaseOrderData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create purchase order"
      );
    }
  }
);

export const fetchPurchaseOrders = createAsyncThunk(
  "inventory/fetchPurchaseOrders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getPurchaseOrders(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch purchase orders"
      );
    }
  }
);

export const fetchPurchaseOrderById = createAsyncThunk(
  "inventory/fetchPurchaseOrderById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getPurchaseOrderById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch purchase order"
      );
    }
  }
);

export const fetchAvailableVendors = createAsyncThunk(
  "inventory/fetchAvailableVendors",
  async (_, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getAvailableVendors();
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch available vendors"
      );
    }
  }
);

export const updatePurchaseOrder = createAsyncThunk(
  "inventory/updatePurchaseOrder",
  async ({ id, purchaseOrderData }, { rejectWithValue }) => {
    try {
      const response = await inventoryService.updatePurchaseOrder(
        id,
        purchaseOrderData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update purchase order"
      );
    }
  }
);

export const deletePurchaseOrder = createAsyncThunk(
  "inventory/deletePurchaseOrder",
  async (id, { rejectWithValue }) => {
    try {
      await inventoryService.deletePurchaseOrder(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete purchase order"
      );
    }
  }
);

// Stock Adjustments
export const createStockAdjustment = createAsyncThunk(
  "inventory/createStockAdjustment",
  async (adjustmentData, { rejectWithValue }) => {
    try {
      const response = await inventoryService.createStockAdjustment(
        adjustmentData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create stock adjustment"
      );
    }
  }
);

export const fetchStockAdjustments = createAsyncThunk(
  "inventory/fetchStockAdjustments",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getStockAdjustments(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch stock adjustments"
      );
    }
  }
);

export const fetchStockAdjustmentById = createAsyncThunk(
  "inventory/fetchStockAdjustmentById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getStockAdjustmentById(id);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch stock adjustment"
      );
    }
  }
);

export const updateStockAdjustment = createAsyncThunk(
  "inventory/updateStockAdjustment",
  async ({ id, adjustmentData }, { rejectWithValue }) => {
    try {
      const response = await inventoryService.updateStockAdjustment(
        id,
        adjustmentData
      );
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update stock adjustment"
      );
    }
  }
);

export const deleteStockAdjustment = createAsyncThunk(
  "inventory/deleteStockAdjustment",
  async (id, { rejectWithValue }) => {
    try {
      await inventoryService.deleteStockAdjustment(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete stock adjustment"
      );
    }
  }
);

// Inventory Overview
export const fetchInventoryOverview = createAsyncThunk(
  "inventory/fetchInventoryOverview",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getInventoryOverview(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch inventory overview"
      );
    }
  }
);

export const fetchInventoryReports = createAsyncThunk(
  "inventory/fetchInventoryReports",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getInventoryReports(params);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch inventory reports"
      );
    }
  }
);

export const fetchLowStockProducts = createAsyncThunk(
  "inventory/fetchLowStockProducts",
  async (threshold = 10, { rejectWithValue }) => {
    try {
      const response = await inventoryService.getLowStockProducts(threshold);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch low stock products"
      );
    }
  }
);

// Bulk Operations
export const bulkStockUpdate = createAsyncThunk(
  "inventory/bulkStockUpdate",
  async (updates, { rejectWithValue }) => {
    try {
      const response = await inventoryService.bulkStockUpdate(updates);
      return response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update stock"
      );
    }
  }
);

const initialState = {
  // Purchase Orders
  purchaseOrders: [],
  currentPurchaseOrder: null,
  isCreatingPurchaseOrder: false,
  isUpdatingPurchaseOrder: false,
  isDeletingPurchaseOrder: false,
  isLoadingPurchaseOrders: false,
  isLoadingPurchaseOrder: false,

  // Stock Adjustments
  stockAdjustments: [],
  currentStockAdjustment: null,
  isCreatingStockAdjustment: false,
  isUpdatingStockAdjustment: false,
  isDeletingStockAdjustment: false,
  isLoadingStockAdjustments: false,

  // Inventory Overview
  inventoryOverview: null,
  inventoryReports: null,
  lowStockProducts: [],
  isLoadingInventoryOverview: false,
  isLoadingInventoryReports: false,
  isLoadingLowStockProducts: false,

  // Bulk Operations
  isBulkUpdating: false,

  // Common
  error: null,
  success: null,
};

const inventorySlice = createSlice({
  name: "inventory",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSuccess: (state) => {
      state.success = null;
    },
    clearCurrentPurchaseOrder: (state) => {
      state.currentPurchaseOrder = null;
    },
    clearCurrentStockAdjustment: (state) => {
      state.currentStockAdjustment = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Purchase Orders
      .addCase(createPurchaseOrder.pending, (state) => {
        state.isCreatingPurchaseOrder = true;
        state.error = null;
      })
      .addCase(createPurchaseOrder.fulfilled, (state, action) => {
        state.isCreatingPurchaseOrder = false;
        const newPurchaseOrder = action.payload.data || action.payload;
        state.purchaseOrders.unshift(newPurchaseOrder);
        state.success = "Purchase order created successfully";
      })
      .addCase(createPurchaseOrder.rejected, (state, action) => {
        state.isCreatingPurchaseOrder = false;
        state.error = action.payload;
      })
      .addCase(fetchPurchaseOrders.pending, (state) => {
        state.isLoadingPurchaseOrders = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrders.fulfilled, (state, action) => {
        state.isLoadingPurchaseOrders = false;
        state.purchaseOrders = Array.isArray(action.payload)
          ? action.payload
          : action.payload.purchaseOrders || action.payload.data || [];
      })
      .addCase(fetchPurchaseOrders.rejected, (state, action) => {
        state.isLoadingPurchaseOrders = false;
        state.error = action.payload;
      })
      .addCase(fetchPurchaseOrderById.pending, (state) => {
        state.isLoadingPurchaseOrder = true;
        state.error = null;
      })
      .addCase(fetchPurchaseOrderById.fulfilled, (state, action) => {
        state.isLoadingPurchaseOrder = false;
        state.currentPurchaseOrder = action.payload.data || action.payload;
      })
      .addCase(fetchPurchaseOrderById.rejected, (state, action) => {
        state.isLoadingPurchaseOrder = false;
        state.error = action.payload;
      })
      .addCase(updatePurchaseOrder.pending, (state) => {
        state.isUpdatingPurchaseOrder = true;
        state.error = null;
      })
      .addCase(updatePurchaseOrder.fulfilled, (state, action) => {
        state.isUpdatingPurchaseOrder = false;
        const updatedPurchaseOrder = action.payload.data || action.payload;
        const index = state.purchaseOrders.findIndex(
          (po) => po.id === updatedPurchaseOrder.id
        );
        if (index !== -1) {
          state.purchaseOrders[index] = updatedPurchaseOrder;
        }
        state.currentPurchaseOrder = updatedPurchaseOrder;
        state.success = "Purchase order updated successfully";
      })
      .addCase(updatePurchaseOrder.rejected, (state, action) => {
        state.isUpdatingPurchaseOrder = false;
        state.error = action.payload;
      })
      .addCase(deletePurchaseOrder.pending, (state) => {
        state.isDeletingPurchaseOrder = true;
        state.error = null;
      })
      .addCase(deletePurchaseOrder.fulfilled, (state, action) => {
        state.isDeletingPurchaseOrder = false;
        state.purchaseOrders = state.purchaseOrders.filter(
          (po) => po.id !== action.payload
        );
        state.success = "Purchase order deleted successfully";
      })
      .addCase(deletePurchaseOrder.rejected, (state, action) => {
        state.isDeletingPurchaseOrder = false;
        state.error = action.payload;
      })
      // Available Vendors
      .addCase(fetchAvailableVendors.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAvailableVendors.fulfilled, (state, action) => {
        state.isLoading = false;
        state.availableVendors = Array.isArray(action.payload)
          ? action.payload
          : action.payload.data || [];
      })
      .addCase(fetchAvailableVendors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Stock Adjustments
      .addCase(createStockAdjustment.pending, (state) => {
        state.isCreatingStockAdjustment = true;
        state.error = null;
      })
      .addCase(createStockAdjustment.fulfilled, (state, action) => {
        state.isCreatingStockAdjustment = false;
        state.stockAdjustments.unshift(action.payload);
        state.success = "Stock adjustment created successfully";
      })
      .addCase(createStockAdjustment.rejected, (state, action) => {
        state.isCreatingStockAdjustment = false;
        state.error = action.payload;
      })
      .addCase(fetchStockAdjustments.pending, (state) => {
        state.isLoadingStockAdjustments = true;
        state.error = null;
      })
      .addCase(fetchStockAdjustments.fulfilled, (state, action) => {
        state.isLoadingStockAdjustments = false;
        state.stockAdjustments = Array.isArray(action.payload)
          ? action.payload
          : action.payload.stockAdjustments || action.payload.data || [];
      })
      .addCase(fetchStockAdjustments.rejected, (state, action) => {
        state.isLoadingStockAdjustments = false;
        state.error = action.payload;
      })
      .addCase(fetchStockAdjustmentById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchStockAdjustmentById.fulfilled, (state, action) => {
        state.currentStockAdjustment = action.payload;
      })
      .addCase(fetchStockAdjustmentById.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(updateStockAdjustment.pending, (state) => {
        state.isUpdatingStockAdjustment = true;
        state.error = null;
      })
      .addCase(updateStockAdjustment.fulfilled, (state, action) => {
        state.isUpdatingStockAdjustment = false;
        const index = state.stockAdjustments.findIndex(
          (sa) => sa.id === action.payload.id
        );
        if (index !== -1) {
          state.stockAdjustments[index] = action.payload;
        }
        state.success = "Stock adjustment updated successfully";
      })
      .addCase(updateStockAdjustment.rejected, (state, action) => {
        state.isUpdatingStockAdjustment = false;
        state.error = action.payload;
      })
      .addCase(deleteStockAdjustment.pending, (state) => {
        state.isDeletingStockAdjustment = true;
        state.error = null;
      })
      .addCase(deleteStockAdjustment.fulfilled, (state, action) => {
        state.isDeletingStockAdjustment = false;
        state.stockAdjustments = state.stockAdjustments.filter(
          (sa) => sa.id !== action.payload
        );
        state.success = "Stock adjustment deleted successfully";
      })
      .addCase(deleteStockAdjustment.rejected, (state, action) => {
        state.isDeletingStockAdjustment = false;
        state.error = action.payload;
      })
      // Inventory Overview
      .addCase(fetchInventoryOverview.pending, (state) => {
        state.isLoadingInventoryOverview = true;
        state.error = null;
      })
      .addCase(fetchInventoryOverview.fulfilled, (state, action) => {
        state.isLoadingInventoryOverview = false;
        const payload = action.payload || {};
        const overview = payload.data?.overview || payload.overview || payload;
        state.inventoryOverview = {
          totalProducts: overview.totalProducts ?? overview.total_products ?? 0,
          totalCategories: overview.totalCategories ?? overview.total_categories ?? 0,
          totalQuantity:
            overview.totalQuantity ?? overview.totalStockQuantity ?? 0,
        };
      })
      .addCase(fetchInventoryOverview.rejected, (state, action) => {
        state.isLoadingInventoryOverview = false;
        state.error = action.payload;
      })
      .addCase(fetchInventoryReports.pending, (state) => {
        state.isLoadingInventoryReports = true;
        state.error = null;
      })
      .addCase(fetchInventoryReports.fulfilled, (state, action) => {
        state.isLoadingInventoryReports = false;
        state.inventoryReports = action.payload;
      })
      .addCase(fetchInventoryReports.rejected, (state, action) => {
        state.isLoadingInventoryReports = false;
        state.error = action.payload;
      })
      .addCase(fetchLowStockProducts.pending, (state) => {
        state.isLoadingLowStockProducts = true;
        state.error = null;
      })
      .addCase(fetchLowStockProducts.fulfilled, (state, action) => {
        state.isLoadingLowStockProducts = false;
        // Handle different response structures
        let products = [];
        if (Array.isArray(action.payload)) {
          products = action.payload;
        } else if (action.payload?.data?.products) {
          products = action.payload.data.products;
        } else if (action.payload?.products) {
          products = action.payload.products;
        } else if (action.payload?.data) {
          products = action.payload.data;
        }
        state.lowStockProducts = products;
      })
      .addCase(fetchLowStockProducts.rejected, (state, action) => {
        state.isLoadingLowStockProducts = false;
        state.error = action.payload;
      })
      // Bulk Operations
      .addCase(bulkStockUpdate.pending, (state) => {
        state.isBulkUpdating = true;
        state.error = null;
      })
      .addCase(bulkStockUpdate.fulfilled, (state, action) => {
        state.isBulkUpdating = false;
        state.success = "Stock updated successfully";
      })
      .addCase(bulkStockUpdate.rejected, (state, action) => {
        state.isBulkUpdating = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  clearSuccess,
  clearCurrentPurchaseOrder,
  clearCurrentStockAdjustment,
} = inventorySlice.actions;

export default inventorySlice.reducer;
